/// <reference types="@types/google.maps" />
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { 
  APIProvider, 
  Map, 
  AdvancedMarker, 
  Pin, 
  useMap 
} from '@vis.gl/react-google-maps';
import { 
  mapsAutocomplete, 
  mapsGeocode, 
  trackMapLoad, 
  getMapsQuota, 
  MapsPrediction, 
  MapsQuotaStatus 
} from '../services/apiServices';
import { useAuth } from '@/src/contexts/AuthContext';

const getInitialKey = (): string => {
  let localKey = '';
  if (typeof window !== 'undefined') {
    try {
      localKey = 
        window.localStorage?.getItem('GOOGLE_MAPS_API_KEY') ||
        window.localStorage?.getItem('VITE_MAPS_RENDER_KEY') ||
        window.localStorage?.getItem('GOOGLE_MAPS_PLATFORM_KEY') ||
        window.localStorage?.getItem('VITE_GOOGLE_MAPS_API_KEY') ||
        window.sessionStorage?.getItem('GOOGLE_MAPS_API_KEY') ||
        (window as any).__MAPS_KEY__ ||
        '';
    } catch {
      // Ignore storage restrictions
    }
  }

  const key = 
    localKey ||
    (import.meta as any).env?.VITE_MAPS_RENDER_KEY ||
    (import.meta as any).env?.GOOGLE_MAPS_PLATFORM_KEY ||
    (import.meta as any).env?.GOOGLE_MAPS_API_KEY ||
    (import.meta as any).env?.VITE_GOOGLE_MAPS_API_KEY ||
    (import.meta as any).env?.MAPS_API_KEY ||
    (import.meta as any).env?.GOOGLE_MAPS_KEY ||
    (typeof process !== 'undefined' && (process.env as any)?.GOOGLE_MAPS_PLATFORM_KEY) ||
    (typeof process !== 'undefined' && (process.env as any)?.GOOGLE_MAPS_API_KEY) ||
    (typeof process !== 'undefined' && (process.env as any)?.VITE_MAPS_RENDER_KEY) ||
    (typeof process !== 'undefined' && (process.env as any)?.VITE_GOOGLE_MAPS_API_KEY) ||
    (typeof process !== 'undefined' && (process.env as any)?.MAPS_API_KEY) ||
    '';
  return (key && key !== 'YOUR_API_KEY') ? key.trim() : '';
};

interface Props {
  onSelect: (locationName: string, coords: { lat: number; lng: number }) => void;
  onClose: () => void;
}

const MapContent: React.FC<Props> = ({ onSelect, onClose }) => {
  const map = useMap();
  const { userProfile } = useAuth();
  const [coords, setCoords] = useState<{ lat: number; lng: number }>({ lat: 0.3476, lng: 32.5825 }); // Default Kampala
  const [searchQuery, setSearchQuery] = useState('');
  const [predictions, setPredictions] = useState<MapsPrediction[]>([]);
  const [showPredictions, setShowPredictions] = useState(false);
  const [locationName, setLocationName] = useState<string | null>(null);
  const [resolving, setResolving] = useState(false);
  const [quotaInfo, setQuotaInfo] = useState<MapsQuotaStatus | null>(null);
  const [quotaExceeded, setQuotaExceeded] = useState(false);
  const [quotaErrorMsg, setQuotaErrorMsg] = useState<string | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // 1. Register Dynamic Map Load and inspect quota
  useEffect(() => {
    let mounted = true;
    async function initQuotaAndLoad() {
      try {
        const loadRes = await trackMapLoad();
        if (!loadRes.allowed) {
          if (mounted) {
            setQuotaExceeded(true);
            setQuotaErrorMsg('Monthly map view quota exceeded for this tier. Upgrade to continue exploring.');
          }
        }
        const quota = await getMapsQuota();
        if (mounted) {
          setQuotaInfo(quota);
          if (quota.categories.dynamicMaps.remaining === 0) {
            setQuotaExceeded(true);
          }
        }
      } catch (err: any) {
        console.warn('Map quota tracking warning:', err);
      }
    }
    initQuotaAndLoad();
    return () => { mounted = false; };
  }, []);

  // 2. Native Geolocation fallback (free browser API)
  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          const newCoords = { lat: pos.coords.latitude, lng: pos.coords.longitude };
          setCoords(newCoords);
          map?.panTo(newCoords);
        },
        () => {
          map?.panTo({ lat: 0.3476, lng: 32.5825 });
        }
      );
    }
  }, [map]);

  // 3. Proxy-based Autocomplete with 400ms debounce
  const fetchPredictions = useCallback(async (query: string) => {
    if (query.length < 3) {
      setPredictions([]);
      return;
    }
    try {
      const data = await mapsAutocomplete(query);
      setPredictions(data.predictions || []);
      setShowPredictions(true);
    } catch (e: any) {
      if (e?.message?.includes('429') || e?.message?.includes('quota')) {
        setQuotaErrorMsg(e.message);
      }
      setPredictions([]);
    }
  }, []);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setSearchQuery(val);
    setLocationName(null);

    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchPredictions(val), 400);
  };

  const handleSelectPrediction = async (prediction: MapsPrediction) => {
    setSearchQuery(prediction.description);
    setPredictions([]);
    setShowPredictions(false);
    setResolving(true);

    try {
      const place = await mapsGeocode(prediction.description);
      const newCoords = { lat: place.lat, lng: place.lng };
      setCoords(newCoords);
      setLocationName(place.address);
      map?.panTo(newCoords);
      map?.setZoom(15);
    } catch (err: any) {
      console.error('Geocode failed:', err);
      if (err?.message?.includes('quota') || err?.message?.includes('429')) {
        setQuotaErrorMsg(err.message);
      }
    } finally {
      setResolving(false);
    }
  };

  const handleConfirm = async () => {
    setResolving(true);
    try {
      let finalName = locationName;
      if (!finalName) {
        // Reverse geocode via proxy
        const geo = await mapsGeocode(`${coords.lat},${coords.lng}`);
        finalName = geo.address;
      }
      onSelect(finalName || `${coords.lat.toFixed(4)}, ${coords.lng.toFixed(4)}`, coords);
    } catch (err: any) {
      onSelect(`${coords.lat.toFixed(4)}, ${coords.lng.toFixed(4)}`, coords);
    } finally {
      setResolving(false);
      onClose();
    }
  };

  const tierName = (userProfile?.subscriptionTier || quotaInfo?.tier || 'free').toUpperCase();
  const mapsRemaining = quotaInfo?.categories?.dynamicMaps?.remaining ?? 5;
  const mapsLimit = quotaInfo?.categories?.dynamicMaps?.limit ?? 5;

  return (
    <div className="w-full h-full flex flex-col relative overflow-hidden bg-[#3a3430] text-white" id="map-targeter-modal">
      {/* Header HUD with Live Quota Meter */}
      <header className="px-6 sm:px-8 py-4 border-b border-white/[0.06] flex flex-col md:flex-row md:items-center justify-between gap-4 relative z-10 bg-[#322D29] shrink-0 text-white">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 bg-[#F26522] rounded flex items-center justify-center text-white shrink-0">
            <i className="fas fa-tower-broadcast text-sm"></i>
          </div>
          <div className="flex flex-col">
            <h2 className="text-base font-bold uppercase tracking-widest text-white font-heading leading-tight">Geospatial Targeter</h2>
            <div className="flex items-center gap-2 mt-0.5">
              <span className="text-[9px] font-bold uppercase tracking-wider text-white/50">Node Resolution Engine</span>
              <div className="flex items-center gap-1.5 px-2 py-0.5 bg-[#F26522]/10 border border-[#F26522]/30 rounded">
                <span className="text-[8px] font-bold uppercase tracking-wider text-[#F26522]">
                  {tierName} TIER • {mapsRemaining} / {mapsLimit === 999999 ? '∞' : mapsLimit} MAP LOADS
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Autocomplete Bar */}
        <div className="flex-1 max-w-md relative">
          <div className="relative group">
            <input 
              type="text"
              value={searchQuery}
              onChange={handleSearchChange}
              onFocus={() => predictions.length > 0 && setShowPredictions(true)}
              placeholder="Query Sector (e.g. Kampala, Nairobi, Lagos)..."
              className="w-full bg-[#3a3430] border border-white/[0.08] px-4 py-2.5 text-xs text-white placeholder-white/40 focus:border-[#F26522] outline-none rounded-lg transition-colors font-medium font-dm"
            />
            <div className="absolute right-3 top-1/2 -translate-y-1/2 w-7 h-7 flex items-center justify-center text-white/40">
              <i className="fas fa-magnifying-glass text-xs"></i>
            </div>
          </div>

          {/* Autocomplete Dropdown */}
          {showPredictions && predictions.length > 0 && (
            <ul className="absolute top-full left-0 right-0 mt-1.5 bg-[#3a3430] border border-white/[0.08] rounded-lg shadow-2xl overflow-hidden z-50 max-h-60 overflow-y-auto custom-scrollbar">
              {predictions.map((p, idx) => (
                <li
                  key={`pred-${p.placeId || 'loc'}-${idx}-${p.description}`}
                  onClick={() => handleSelectPrediction(p)}
                  className="px-4 py-2.5 text-xs font-medium text-white hover:bg-white/5 hover:text-[#F26522] cursor-pointer border-b border-white/[0.04] last:border-0 flex items-center justify-between"
                >
                  <div className="flex items-center gap-2.5">
                    <i className="fas fa-location-dot text-[#F26522] text-[10px]"></i>
                    <span>{p.description}</span>
                  </div>
                  <i className="fas fa-arrow-right text-[9px] text-white/30"></i>
                </li>
              ))}
            </ul>
          )}
        </div>

        <button 
          onClick={onClose} 
          className="w-8 h-8 flex items-center justify-center text-white/40 hover:text-white rounded-lg hover:bg-white/5 transition-colors cursor-pointer"
          aria-label="Close Targeter"
        >
          <i className="fas fa-xmark text-sm"></i>
        </button>
      </header>

      {/* Map Area */}
      <div className="flex-1 relative bg-[#2a2522] min-h-[400px]">
        {quotaExceeded ? (
          <div className="absolute inset-0 z-30 bg-black/75 flex items-center justify-center p-6 text-white text-center">
            <div className="max-w-md w-full bg-[#3a3430] p-8 border border-white/[0.06] rounded-xl text-white shadow-2xl space-y-5 animate-in zoom-in-95 duration-300">
              <div className="w-12 h-12 bg-[#F26522]/10 text-[#F26522] border border-[#F26522]/30 rounded-xl flex items-center justify-center mx-auto text-xl">
                <i className="fas fa-lock"></i>
              </div>
              <div className="space-y-2">
                <h3 className="text-base font-bold uppercase tracking-tight font-heading text-white">Geospatial Quota Reached</h3>
                <p className="text-xs font-medium text-white/70 leading-relaxed">
                  {quotaErrorMsg || `You've used all allocated map views for your ${tierName} plan this billing cycle.`}
                </p>
              </div>
              <div className="pt-2 flex flex-col gap-2.5">
                <button
                  onClick={() => {
                    onClose();
                    window.dispatchEvent(new CustomEvent('trigger-upgrade'));
                  }}
                  className="w-full py-3.5 bg-[#F26522] hover:bg-[#F26522]/90 text-white font-bold uppercase text-xs tracking-widest rounded-lg transition-all cursor-pointer"
                >
                  Upgrade to Expand Quotas
                </button>
                <button
                  onClick={onClose}
                  className="w-full py-3 bg-white/5 hover:bg-white/10 text-white/70 border border-white/10 font-bold uppercase text-[10px] tracking-wider rounded-lg transition-colors cursor-pointer"
                >
                  Return to Dashboard
                </button>
              </div>
            </div>
          </div>
        ) : null}

        <Map
          defaultCenter={coords}
          defaultZoom={13}
          mapId="DEMO_MAP_ID"
          onCenterChanged={(e) => {
            const newCenter = e.map.getCenter();
            if (newCenter) {
              setCoords({ lat: newCenter.lat(), lng: newCenter.lng() });
              setLocationName(null);
            }
          }}
          style={{ width: '100%', height: '100%' }}
          gestureHandling={'greedy'}
          disableDefaultUI={true}
        >
          <AdvancedMarker position={coords}>
            <Pin background="#F26522" glyphColor="#fff" borderColor="#322D29" />
          </AdvancedMarker>
        </Map>

        {/* HUD Elements Overlay */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden z-10">
          <div className="absolute top-1/2 left-0 w-full h-px bg-white/[0.04]"></div>
          <div className="absolute left-1/2 top-0 w-px h-full bg-white/[0.04]"></div>
          <div className="absolute top-6 left-6 w-8 h-8 border-t-2 border-l-2 border-white/20"></div>
          <div className="absolute top-6 right-6 w-8 h-8 border-t-2 border-r-2 border-white/20"></div>
          <div className="absolute bottom-6 left-6 w-8 h-8 border-b-2 border-l-2 border-white/20"></div>
          <div className="absolute bottom-6 right-6 w-8 h-8 border-b-2 border-r-2 border-white/20"></div>
        </div>

        {/* Coordinates Display HUD */}
        <div className="absolute bottom-6 left-6 p-4 bg-[#322D29]/95 border border-white/[0.06] rounded-xl backdrop-blur-md shadow-xl space-y-3 max-w-xs w-full z-20 text-white">
          <div>
            <span className="text-[8px] font-bold uppercase text-[#F26522] tracking-[0.25em] block mb-1">Target Coordinates</span>
            <div className="flex gap-4">
              <div>
                <span className="text-[7px] font-bold text-white/40 block uppercase">LAT</span>
                <span className="text-xs font-bold text-white tabular-nums font-heading">{coords.lat.toFixed(6)}</span>
              </div>
              <div className="w-px h-6 bg-white/10"></div>
              <div>
                <span className="text-[7px] font-bold text-white/40 block uppercase">LNG</span>
                <span className="text-xs font-bold text-white tabular-nums font-heading">{coords.lng.toFixed(6)}</span>
              </div>
            </div>
          </div>

          {locationName && (
            <div className="pt-2.5 border-t border-white/[0.06] flex items-start gap-2">
              <i className="fas fa-location-dot text-[#F26522] text-xs mt-0.5"></i>
              <span className="text-[11px] font-medium text-white/80 leading-snug line-clamp-2">{locationName}</span>
            </div>
          )}
        </div>
      </div>

      {/* Footer */}
      <footer className="p-4 sm:p-5 border-t border-white/[0.06] bg-[#322D29] flex flex-col sm:flex-row gap-3 relative z-10 shrink-0">
        <button 
          onClick={onClose}
          className="sm:w-32 px-5 py-3 border border-white/10 text-white/70 hover:text-white hover:bg-white/5 font-bold uppercase text-[10px] tracking-[0.2em] rounded-lg transition-colors cursor-pointer"
        >
          Cancel
        </button>
        <button 
          onClick={handleConfirm}
          disabled={resolving || quotaExceeded}
          className="flex-1 bg-[#F26522] hover:bg-[#F26522]/90 text-white py-3 font-bold uppercase text-[10px] tracking-[0.25em] rounded-lg transition-all disabled:opacity-30 flex items-center justify-center gap-2 cursor-pointer shadow-none"
        >
          {resolving ? (
            <>
              <div className="w-3.5 h-3.5 border-2 border-white/20 border-t-white rounded-full animate-spin"></div>
              Resolving Geographic Blueprint...
            </>
          ) : (
            <>
              <i className="fas fa-location-arrow text-xs"></i>
              Deploy Node Coordinates
            </>
          )}
        </button>
      </footer>
    </div>
  );
};

const MapPicker: React.FC<Props> = (props) => {
  const [apiKey, setApiKey] = useState<string>(getInitialKey);
  const [isCheckingConfig, setIsCheckingConfig] = useState<boolean>(!getInitialKey());
  const [manualKey, setManualKey] = useState<string>('');
  const [keyError, setKeyError] = useState<string>('');

  const fetchServerConfig = useCallback(() => {
    setIsCheckingConfig(true);
    setKeyError('');
    fetch('/api/maps/config')
      .then((res) => res.json())
      .then((data) => {
        if (data.hasKey && data.apiKey) {
          setApiKey(data.apiKey);
          if (typeof window !== 'undefined') {
            (window as any).__MAPS_KEY__ = data.apiKey;
            try {
              localStorage.setItem('GOOGLE_MAPS_API_KEY', data.apiKey);
            } catch {}
          }
        } else {
          setKeyError('Server reported no API key found in environment or functions/.env.');
        }
      })
      .catch((err) => {
        setKeyError(`Failed to reach /api/maps/config: ${err.message}`);
      })
      .finally(() => {
        setIsCheckingConfig(false);
      });
  }, []);

  const handleManualActivate = () => {
    const clean = manualKey.trim();
    if (!clean) return;
    setApiKey(clean);
    if (typeof window !== 'undefined') {
      (window as any).__MAPS_KEY__ = clean;
      try {
        localStorage.setItem('GOOGLE_MAPS_API_KEY', clean);
        localStorage.setItem('VITE_MAPS_RENDER_KEY', clean);
      } catch {}
    }
  };

  useEffect(() => {
    if (!apiKey) {
      fetchServerConfig();
    } else {
      setIsCheckingConfig(false);
    }
  }, [apiKey, fetchServerConfig]);

  if (isCheckingConfig) {
    return (
      <div className="fixed inset-0 z-[400] bg-black/75 flex items-center justify-center p-6 text-white">
        <div className="bg-[#3a3430] border border-white/[0.06] rounded-xl p-8 text-center space-y-4 max-w-sm w-full">
          <div className="w-9 h-9 border-2 border-[#F26522] border-t-transparent rounded-full animate-spin mx-auto"></div>
          <p className="text-xs text-white/70 tracking-widest uppercase font-semibold">Verifying Maps API Key...</p>
        </div>
      </div>
    );
  }

  if (!apiKey || apiKey === 'YOUR_API_KEY') {
    return (
      <div className="fixed inset-0 z-[400] bg-black/75 flex items-center justify-center p-6 text-white">
        <div className="max-w-md w-full bg-[#3a3430] border border-white/[0.06] rounded-xl p-8 text-center space-y-6 animate-in fade-in zoom-in-95 duration-300">
          <div className="w-14 h-14 bg-[#F26522]/10 text-[#F26522] border border-[#F26522]/30 rounded-xl flex items-center justify-center mx-auto">
            <i className="fas fa-key text-xl text-[#F26522]"></i>
          </div>
          <div className="space-y-2">
            <h2 className="text-xl font-bold uppercase tracking-tight font-heading text-white">API Key Required</h2>
            <p className="text-xs text-white/60 font-medium leading-relaxed">
              To enable live geospatial mapping, configure your Google Maps API key in environment or secrets.
            </p>
          </div>
          
          <div className="bg-[#322D29] p-5 border border-white/[0.06] rounded-lg text-left space-y-3">
            <div className="flex gap-3">
              <div className="w-5 h-5 rounded bg-[#F26522] text-white flex items-center justify-center text-[9px] font-bold shrink-0">1</div>
              <p className="text-xs text-white/70">
                Get an API key from the <a href="https://console.cloud.google.com/google/maps-apis/credentials?utm_campaign=gmp_mcp_codeassist_v1_aistudio" target="_blank" rel="noopener noreferrer" className="text-[#F26522] font-bold underline">Google Cloud Console</a>.
              </p>
            </div>
            <div className="flex gap-3">
              <div className="w-5 h-5 rounded bg-[#F26522] text-white flex items-center justify-center text-[9px] font-bold shrink-0">2</div>
              <p className="text-xs text-white/70">Enable <strong>Maps JavaScript API, Places API, and Geocoding API</strong>.</p>
            </div>
            <div className="flex gap-3">
              <div className="w-5 h-5 rounded bg-[#F26522] text-white flex items-center justify-center text-[9px] font-bold shrink-0">3</div>
              <p className="text-xs text-white/70">
                Set <code>GOOGLE_MAPS_PLATFORM_KEY</code>, <code>GOOGLE_MAPS_API_KEY</code>, or <code>VITE_MAPS_RENDER_KEY</code> in Settings &gt; Secrets / Environment or in <code>functions/.env</code>.
              </p>
            </div>

            <div className="pt-3 border-t border-white/[0.06] space-y-2">
              <label className="text-[10px] uppercase font-bold tracking-wider text-white/50 block">
                Instant Key Activation (Paste &amp; Activate):
              </label>
              <div className="flex gap-2">
                <input
                  type="password"
                  placeholder="AIzaSy..."
                  value={manualKey}
                  onChange={(e) => setManualKey(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') handleManualActivate(); }}
                  className="flex-1 bg-[#25211E] border border-white/10 rounded-lg px-3 py-2 text-xs text-white placeholder:text-white/30 focus:outline-none focus:border-[#F26522]"
                />
                <button
                  type="button"
                  onClick={handleManualActivate}
                  disabled={!manualKey.trim()}
                  className="px-4 py-2 bg-[#F26522] hover:bg-[#F26522]/90 disabled:opacity-40 text-white text-xs font-bold uppercase rounded-lg transition-colors cursor-pointer"
                >
                  Activate
                </button>
              </div>
            </div>
          </div>

          {keyError && (
            <p className="text-xs text-[#F26522] font-medium bg-[#F26522]/10 border border-[#F26522]/20 rounded-lg p-3 text-left">
              {keyError}
            </p>
          )}

          <div className="flex flex-col gap-3">
            <button 
              onClick={fetchServerConfig}
              className="w-full py-3.5 font-bold uppercase text-[10px] tracking-[0.25em] bg-[#F26522] hover:bg-[#F26522]/90 text-white rounded-lg transition-colors cursor-pointer flex items-center justify-center gap-2"
            >
              <i className="fas fa-rotate-right text-xs"></i>
              Re-check Key Status
            </button>
            <button 
              onClick={props.onClose}
              className="w-full py-3 font-bold uppercase text-[10px] tracking-[0.25em] bg-white/5 hover:bg-white/10 text-white/80 border border-white/10 rounded-lg transition-colors cursor-pointer"
            >
              Close Targeter
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-[400] bg-black/75 flex items-center justify-center p-0 sm:p-4 md:p-8 overflow-hidden animate-in fade-in duration-200">
      <div className="w-full h-full max-w-6xl bg-[#3a3430] border border-white/[0.06] rounded-xl shadow-2xl flex flex-col relative overflow-hidden animate-in zoom-in-95 duration-200 text-white">
        <APIProvider apiKey={apiKey} version="weekly">
          <MapContent {...props} />
        </APIProvider>
      </div>
    </div>
  );
};

export default MapPicker;
